import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Plus, Search, Download, Trash2, Eye, Pencil, MoreVertical } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';

const FY_OPTIONS = [
  'all', 'FY 2025-26', 'FY 2024-25', 'FY 2023-24', 'FY 2022-23', 'FY 2021-22',
];

export default function ClientsPage() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [fyFilter, setFyFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState([]);

  const fetchClients = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (fyFilter !== 'all') params.financial_year = fyFilter;
      const { data } = await api.get('/clients', { params });
      setClients(data.clients);
      setTotal(data.total);
    } catch {
      toast.error('Failed to load clients');
    }
    setLoading(false);
  }, [page, search, statusFilter, fyFilter]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const toggleSelect = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selected.length === clients.length) {
      setSelected([]);
    } else {
      setSelected(clients.map((c) => c.id));
    }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} client(s)?`)) return;
    try {
      await api.post('/clients/bulk-delete', { ids: selected });
      toast.success(`Deleted ${selected.length} clients`);
      setSelected([]);
      fetchClients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this client?')) return;
    try {
      await api.delete(`/clients/${id}`);
      toast.success('Client deleted');
      fetchClients();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const exportCSV = () => {
    if (!clients.length) return;
    const headers = ['Trust ID', 'Trust Name', 'Trustee Name', 'Email', 'Phone', 'Category', 'Status', 'Created'];
    const rows = clients.map((c) => [
      c.trust_id, c.trust_name, c.trustee_name, c.email, c.phone, c.category, c.status,
      c.created_at ? new Date(c.created_at).toLocaleDateString() : '',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((v) => `"${(v || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'clients.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalPages = Math.ceil(total / 50);

  return (
    <div className="space-y-4" data-testid="clients-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('clients')}
        </h1>
        <Button onClick={() => navigate('/clients/new')} data-testid="add-client-btn">
          <Plus className="h-4 w-4 mr-1.5" /> {t('add_client')}
        </Button>
      </div>

      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="pl-9"
            data-testid="client-search"
          />
        </div>
        <Select value={fyFilter} onValueChange={(v) => { setFyFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40" data-testid="fy-filter">
            <SelectValue placeholder={t('select_fy')} />
          </SelectTrigger>
          <SelectContent>
            {FY_OPTIONS.map((fy) => (
              <SelectItem key={fy} value={fy}>{fy === 'all' ? t('all') : fy}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="status-filter">
            <SelectValue placeholder={t('select_status')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('all')}</SelectItem>
            <SelectItem value="active">{t('active')}</SelectItem>
            <SelectItem value="inactive">{t('inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
          <Download className="h-3.5 w-3.5 mr-1.5" /> {t('export_csv')}
        </Button>
        {selected.length > 0 && user?.role === 'admin' && (
          <Button variant="destructive" size="sm" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
            <Trash2 className="h-3.5 w-3.5 mr-1.5" /> {t('bulk_delete')} ({selected.length})
          </Button>
        )}
      </div>

      <div className="rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10">
                <Checkbox
                  checked={clients.length > 0 && selected.length === clients.length}
                  onCheckedChange={toggleAll}
                  data-testid="select-all-checkbox"
                />
              </TableHead>
              <TableHead>{t('trust_id')}</TableHead>
              <TableHead>{t('trust_name')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('trustee_name')}</TableHead>
              <TableHead className="hidden lg:table-cell">{t('email')}</TableHead>
              <TableHead className="hidden lg:table-cell">{t('phone')}</TableHead>
              <TableHead>{t('status')}</TableHead>
              <TableHead className="hidden md:table-cell">{t('date_of_creation')}</TableHead>
              <TableHead className="w-12">{t('actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto" />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                  {t('no_data')}
                </TableCell>
              </TableRow>
            ) : (
              clients.map((c) => (
                <TableRow key={c.id} data-testid={`client-row-${c.trust_id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.includes(c.id)}
                      onCheckedChange={() => toggleSelect(c.id)}
                    />
                  </TableCell>
                  <TableCell className="font-mono text-xs tabular-nums">{c.trust_id}</TableCell>
                  <TableCell className="font-medium">{c.trust_name || '-'}</TableCell>
                  <TableCell className="hidden md:table-cell">{c.trustee_name || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm">{c.email || '-'}</TableCell>
                  <TableCell className="hidden lg:table-cell text-sm tabular-nums">{c.phone || '-'}</TableCell>
                  <TableCell>
                    <Badge
                      variant={c.status === 'active' ? 'default' : 'secondary'}
                      className={
                        c.status === 'active'
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 hover:bg-emerald-100'
                          : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 hover:bg-slate-100'
                      }
                      data-testid={`status-badge-${c.trust_id}`}
                    >
                      {c.status === 'active' ? t('active') : t('inactive')}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-xs text-muted-foreground tabular-nums">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`actions-${c.trust_id}`}>
                          <MoreVertical className="h-3.5 w-3.5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => navigate(`/clients/${c.id}`)}>
                          <Eye className="h-3.5 w-3.5 mr-2" /> {t('view')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/clients/${c.id}/edit`)}>
                          <Pencil className="h-3.5 w-3.5 mr-2" /> {t('edit')}
                        </DropdownMenuItem>
                        {user?.role === 'admin' && (
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleDelete(c.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5 mr-2" /> {t('delete')}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <p className="text-xs text-muted-foreground">
              Showing {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} of {total}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => p - 1)}
                data-testid="prev-page"
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
                data-testid="next-page"
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
